import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { fromEvent } from 'rxjs';
import { DocumentationComponent } from 'src/app/views/layout/documentation/documentation.component';
interface MenuItem {
  id?: number;
  label?: string;
  icon?: string;
  link?: string;
  expanded?: boolean;
  subItems?: MenuItem[];
  isTitle?: boolean;
  badge?: any;
  parentId?: number;
  disabled?: boolean;
}
@Injectable({
  providedIn: 'root',
})
export class KeyboardShortcutsService {
  shortcutsMenu: MenuItem[] = [];

  private firstKeyPressed: number | null = null;
  private isCtrlPressed = false;
  private isWaitingForLetter = false;

  constructor(private offcanvasService: NgbOffcanvas, private router: Router) {
    fromEvent<KeyboardEvent>(window, 'keydown').subscribe((event) => {
      this.shortcutsMenu = this.removeDisabledItems(this.shortcutsMenu);
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const isCtrlOrCmd = isMac ? event.metaKey : event.ctrlKey;
      // Track if Ctrl or Cmd is pressed
      if (isCtrlOrCmd) {
        this.isCtrlPressed = true;
      }
      if (
        this.isCtrlPressed &&
        event.key.toLowerCase() === 'd' &&
        this.firstKeyPressed === null
      ) {
        event.preventDefault();
        this.openDocumentation();
        return;
      }
      // Handle number keys followed by another key (e.g., B, E, D)
      const numberMatch = event.code.match(
        /Digit([1-9])|Numpad([1-9])|Key([A-Za-z])/
      );

      if (this.isCtrlPressed && numberMatch) {
        event.preventDefault();
        const number = parseInt(numberMatch[1] || numberMatch[2]);
        const letter = numberMatch[3] ? numberMatch[3].toLowerCase() : null;
        let convertedNumber: number | null = null;

        // Handle letter to number conversion if it's a letter after a number key
        if (letter) {
          convertedNumber = letter.charCodeAt(0) - 'a'.charCodeAt(0) + 1;
        }

        if (this.firstKeyPressed === null && !letter) {
          // First key is a number, allow navigation to that menu item
          const menuItem = this.shortcutsMenu[number - 1];
          if (menuItem && !menuItem.disabled) {
            if (menuItem.link) {
              // Direct navigation for items without subitems
              this.router.navigate([menuItem.link]);
            } else if (menuItem.subItems && menuItem.subItems.length > 0) {
              // Navigate to first subitem for items with subitems
              this.router.navigate([menuItem.subItems[0].link]);
            }
            this.firstKeyPressed = number;
            this.isWaitingForLetter = true; // Set state to wait for a letter
          }
        } else if (this.isWaitingForLetter && letter) {
          event.preventDefault();

          // After a number, handle letter keypress (e.g., Ctrl+2+B)
          const parentIndex = this.firstKeyPressed! - 1;
          const subItemIndex = convertedNumber! - 1;

          const menuItem = this.shortcutsMenu[parentIndex];

          if (
            menuItem &&
            !menuItem.disabled &&
            menuItem.subItems &&
            menuItem.subItems[subItemIndex] &&
            !menuItem.subItems[subItemIndex].disabled
          ) {
            const subItem = menuItem.subItems[subItemIndex];
            if (subItem.link) {
              this.router.navigate([subItem.link]);
            }
          }

          // Reset the sequence after handling
          this.firstKeyPressed = null;
          this.isWaitingForLetter = false;
        }
      }

      document.addEventListener('keyup', (event: KeyboardEvent) => {
        if (event.key === 'Control' || event.key === 'Meta') {
          this.isCtrlPressed = false;
          this.firstKeyPressed = null;
          this.isWaitingForLetter = false;
        }
      });
    });
  }

  openDocumentation() {
    const activeInstance = this.offcanvasService.hasOpenOffcanvas();
    if (activeInstance) {
      this.offcanvasService.dismiss(activeInstance);
    } else {
      this.offcanvasService.open(DocumentationComponent);
    }
  }
  private removeDisabledItems = (menuItems: MenuItem[]): MenuItem[] => {
    return menuItems
      .filter((item) => !item.disabled)
      .map((item) => {
        if (item.subItems) {
          return {
            ...item,
            subItems: item.subItems.filter((subItem) => !subItem.disabled),
          };
        }
        return item;
      });
  };
}
